import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth.jsx';
import { useSocket } from '../hooks/useSocket.jsx';
import { EmployeeHeader } from '../components/EmployeeHeader.jsx';
import { TodaysScreenshotsPreview } from '../components/TodaysScreenshotsPreview.jsx';
import { LateCheckInModal } from '../components/LateCheckInModal.jsx';
import { EndOfDayNoteModal } from '../components/EndOfDayNoteModal.jsx';
import { attendanceApi } from '../api/endpoints.js';
import { ApiError, api } from '../api/client.js';
import { formatHourMinute } from '../lib/time.js';

export default function DashboardPage() {
    const { user, logout } = useAuth();
    const queryClient = useQueryClient();
    const { connected: socketConnected } = useSocket();

    // Live local clock — re-renders the elapsed timer once a minute.
    const [, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 30 * 1000);
        return () => clearInterval(id);
    }, []);

    const current = useQuery({
        queryKey: ['attendance', 'current'],
        queryFn: () => attendanceApi.current(),
        refetchInterval: 60 * 1000,
    });

    const recent = useQuery({
        queryKey: ['attendance', 'recent', 7],
        queryFn: () => attendanceApi.recent(7),
        refetchInterval: 5 * 60 * 1000,
    });

    const balance = useQuery({
        queryKey: ['leave', 'balance'],
        queryFn: () => api.get('/api/v1/leave/balance'),
    });

    const leaveHistory = useQuery({
        queryKey: ['leave', 'mine', 'recent'],
        queryFn: () => api.get('/api/v1/leave/requests?page=1'),
    });

    const [submitting, setSubmitting] = useState(false);
    const [actionError, setActionError] = useState(null);
    const [lateModal, setLateModal] = useState({ open: false, contextLine: null });
    const [eodModal, setEodModal] = useState({ open: false });

    const refresh = () => {
        queryClient.invalidateQueries({ queryKey: ['attendance'] });
    };

    // Refresh when socket reconnects
    useEffect(() => {
        if (socketConnected) refresh();
    }, [socketConnected]);

    // -------- Check-in flow --------
    const tryCheckIn = async (lateReason = null) => {
        setSubmitting(true);
        setActionError(null);
        try {
            await attendanceApi.checkIn(lateReason);
            setLateModal({ open: false, contextLine: null });
            refresh();
        } catch (err) {
            if (err instanceof ApiError) {
                if (err.code === 'LATE_REASON_REQUIRED') {
                    setLateModal({
                        open: true,
                        contextLine: err.details?.context_line ?? null,
                    });
                } else {
                    setActionError(err.message);
                }
            } else {
                setActionError('Could not check in. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    // -------- Check-out flow --------
    const handleCheckOutClick = () => setEodModal({ open: true });
    const performCheckOut = async (note) => {
        setSubmitting(true);
        setActionError(null);
        try {
            await attendanceApi.checkOut(note);
            setEodModal({ open: false });
            refresh();
        } catch (err) {
            if (err instanceof ApiError) setActionError(err.message);
            else setActionError('Could not check out. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (current.isLoading) return <CenteredMessage text="Loading…" />;
    if (current.isError) {
        return (
            <CenteredMessage
                text={current.error?.message || 'Could not load. Please refresh.'}
                tone="error"
            />
        );
    }

    const data = current.data;
    const checkedIn = !!data?.checked_in;
    const shift = data?.shift_today;
    const elapsedMin = data?.open_session?.elapsed_minutes ?? 0;
    const todayHours = data?.attendance_today?.total_hours ?? 0;
    const targetHours = 8;
    const targetMinutes = targetHours * 60;
    const totalMinutesToday = checkedIn ? elapsedMin + Math.round(todayHours * 60) : Math.round(todayHours * 60);
    const progressPct = Math.min(100, Math.round((totalMinutesToday / targetMinutes) * 100));
    const remaining = Math.max(0, targetMinutes - totalMinutesToday);

    const sessionsToday = data?.attendance_today?.sessions_count ?? null;
    const checkInTimeStr = data?.open_session?.check_in_time
        ? new Date(data.open_session.check_in_time).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
        : null;

    const weekHours = data?.this_week?.total_hours ?? null;
    const weekTarget = data?.this_week?.target_hours ?? 40;

    return (
        <>
            <div className="max-w-3xl mx-auto px-4 py-6">

                {/* ----- Header ----- */}
                <EmployeeHeader />

                {/* ----- Currently checked in card ----- */}
                <section className="surface-card p-5 mb-4">
                    <div className="flex items-start justify-between mb-3.5">
                        <div>
                            <p className="eyebrow m-0">
                                {checkedIn ? 'Currently checked in' : 'Not checked in'}
                            </p>
                            <p className="tnum" style={{ fontSize: 32, fontWeight: 500, marginTop: 4 }}>
                                {formatHourMinute(totalMinutesToday)}
                            </p>
                            <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                                {checkedIn
                                    ? buildSessionMeta(checkInTimeStr, sessionsToday)
                                    : "Tap below when you're ready to start"}
                            </p>
                        </div>
                        {checkedIn ? (
                            <button
                                onClick={handleCheckOutClick}
                                disabled={submitting}
                                className="btn btn-danger"
                            >
                                {submitting ? 'Checking out…' : 'Check out'}
                            </button>
                        ) : (
                            <button
                                onClick={() => tryCheckIn(null)}
                                disabled={submitting}
                                className="btn btn-primary"
                            >
                                {submitting ? 'Checking in…' : 'Check in'}
                            </button>
                        )}
                    </div>

                    {/* Progress bar */}
                    <div style={{
                        height: 6,
                        background: 'var(--color-bg-secondary)',
                        borderRadius: 3,
                        overflow: 'hidden',
                        marginBottom: 6,
                    }}>
                        <div style={{
                            width: `${progressPct}%`,
                            height: '100%',
                            background: 'var(--color-status-complete)',
                            transition: 'width 300ms ease',
                        }} />
                    </div>
                    <div className="flex justify-between"
                        style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                        <span>{formatHourMinute(totalMinutesToday)} of {targetHours}h target</span>
                        <span>{formatHourMinute(remaining)} remaining</span>
                    </div>
                </section>

                {actionError && (
                    <div className="surface-card p-3 mb-4" style={{
                        background: 'var(--color-pill-rejected-bg)',
                        borderColor: 'var(--color-danger-border)',
                        color: 'var(--color-danger)',
                        fontSize: 13,
                    }}>
                        {actionError}
                    </div>
                )}

                {/* ----- Shift / week tiles ----- */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                    <SoftTile
                        label="Shift today"
                        value={shift ? `${tidyTime(shift.start)} – ${tidyTime(shift.end)}` : '—'}
                    />
                    <SoftTile
                        label="This week"
                        value={weekHours !== null ? `${formatHourMinute(weekHours * 60)} / ${weekTarget}h` : `— / ${weekTarget}h`}
                    />
                </div>

                {/* ----- Last 7 days strip ----- */}
                <p className="font-medium mb-2.5" style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
                    Last 7 days
                </p>
                <DayStrip days={recent.data?.days || []} loading={recent.isLoading} />
                <DayStripLegend />

                {/* ----- Leave (balance + recent) ----- */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <LeaveBalanceCard data={balance.data} loading={balance.isLoading} />
                    <RequestLeaveCta />
                </div>

                <TodaysScreenshotsPreview />

                <RecentLeaveRequestsCard
                    history={leaveHistory.data}
                    loading={leaveHistory.isLoading}
                />
            </div>

            <LateCheckInModal
                open={lateModal.open}
                contextLine={lateModal.contextLine}
                onConfirm={(reason) => tryCheckIn(reason)}
                onDismiss={() => setLateModal({ open: false, contextLine: null })}
            />
            <EndOfDayNoteModal
                open={eodModal.open}
                totalHoursLabel={formatHourMinute(totalMinutesToday)}
                sessionsCount={sessionsToday ?? 1}
                onCheckOut={(note) => performCheckOut(note)}
                onDismiss={() => setEodModal({ open: false })}
            />
        </>
    );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SoftTile({ label, value }) {
    return (
        <div className="surface-soft" style={{ padding: '12px 14px' }}>
            <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', margin: 0 }}>{label}</p>
            <p className="tnum" style={{ fontSize: 15, fontWeight: 500, marginTop: 2 }}>
                {value}
            </p>
        </div>
    );
}

function DayStrip({ days, loading }) {
    if (loading || !days || days.length === 0) {
        return (
            <div className="grid gap-1.5 mb-2" style={{ gridTemplateColumns: 'repeat(7, 1fr)' }}>
                {Array.from({ length: 7 }, (_, i) => (
                    <div key={i} className="day-strip-cell" style={{ minHeight: 64, opacity: 0.5 }} />
                ))}
            </div>
        );
    }

    return (
        <div className="grid gap-1.5 mb-2" style={{ gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {days.map((d) => (
                <DayCell key={d.date} day={d} />
            ))}
        </div>
    );
}

function DayCell({ day }) {
    const barColor = barColorFor(day);
    const labelText = labelTextFor(day);
    const isToday = day.is_today;
    const dayNumColor = isToday ? 'var(--color-status-today-deep)' : 'var(--color-text-primary)';
    const weekdayColor = isToday ? 'var(--color-status-today-deep)' : 'var(--color-text-tertiary)';
    const labelColor = isToday ? 'var(--color-status-today-deep)' : 'var(--color-text-tertiary)';

    return (
        <div className={`day-strip-cell ${isToday ? 'is-today' : ''}`}>
            <p style={{ fontSize: 10, margin: 0, textTransform: 'uppercase', color: weekdayColor }}>
                {day.weekday}
            </p>
            <p style={{ fontSize: 13, fontWeight: 500, margin: '2px 0', color: dayNumColor }} className="tnum">
                {day.day}
            </p>
            <div className="day-strip-bar" style={{ background: barColor }} />
            <p style={{ fontSize: 11, fontWeight: labelText.bold ? 500 : 400, margin: 0, color: labelColor }}>
                {labelText.text}
            </p>
        </div>
    );
}

function barColorFor(day) {
    switch (day.status) {
        case 'complete':   return 'var(--color-status-complete)';
        case 'incomplete': return 'var(--color-status-incomplete)';
        case 'on_leave':   return 'var(--color-status-on-leave)';
        case 'today':      return 'var(--color-status-today)';
        case 'absent':     return 'var(--color-status-absent)';
        case 'weekend':
        case 'holiday':
        case 'future':
        default:           return 'var(--color-status-weekend)';
    }
}

function labelTextFor(day) {
    if (day.status === 'today') {
        if (day.hours > 0) return { text: formatHourMinuteShort(day.hours * 60), bold: true };
        return { text: 'Today', bold: true };
    }
    if (day.status === 'complete' || day.status === 'incomplete') {
        return { text: formatHourMinuteShort((day.hours || 0) * 60), bold: true };
    }
    if (day.status === 'on_leave') return { text: capitalise(day.label || 'Leave'), bold: false };
    if (day.status === 'weekend') return { text: 'Weekend', bold: false };
    if (day.status === 'holiday') return { text: day.label || 'Holiday', bold: false };
    if (day.status === 'absent') return { text: 'Absent', bold: false };
    return { text: '—', bold: false };
}

function formatHourMinuteShort(totalMinutes) {
    const h = Math.floor(totalMinutes / 60);
    const m = Math.round(totalMinutes - h * 60);
    return `${h}h ${m.toString().padStart(2, '0')}m`;
}

function capitalise(s) {
    if (!s) return '';
    return s.charAt(0).toUpperCase() + s.slice(1);
}

function DayStripLegend() {
    const items = [
        ['Complete (≥7h 40m)', 'var(--color-status-complete)'],
        ['Incomplete', 'var(--color-status-incomplete)'],
        ['On leave', 'var(--color-status-on-leave)'],
        ['Today', 'var(--color-status-today)'],
        ['Weekend / Holiday', 'var(--color-status-weekend)'],
    ];
    return (
        <div className="flex flex-wrap mb-6" style={{
            gap: 12,
            fontSize: 11,
            color: 'var(--color-text-secondary)',
            marginTop: -10,
        }}>
            {items.map(([text, color]) => (
                <span key={text} className="flex items-center" style={{ gap: 4 }}>
                    <span style={{
                        display: 'inline-block',
                        width: 8,
                        height: 4,
                        background: color,
                        borderRadius: 1,
                    }} />
                    {text}
                </span>
            ))}
        </div>
    );
}

function LeaveBalanceCard({ data, loading }) {
    if (loading || !data) {
        return (
            <div className="surface-card" style={{ padding: '1rem 1.25rem' }}>
                <p style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>Leave balance</p>
                <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 8 }}>Loading…</p>
            </div>
        );
    }
    const sp = data.shared_pool;
    const yearLabel = formatYearRange(data.leave_year_start);
    const usedPct = sp.allocated > 0 ? Math.round((sp.used / sp.allocated) * 100) : 0;
    return (
        <div className="surface-card" style={{ padding: '1rem 1.25rem' }}>
            <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
                <p style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>Leave balance</p>
                <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{yearLabel}</span>
            </div>
            <div className="flex items-baseline" style={{ gap: 6, marginBottom: 4 }}>
                <span className="tnum" style={{ fontSize: 28, fontWeight: 500 }}>{sp.remaining}</span>
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                    of {sp.allocated} days remaining
                </span>
            </div>
            <div style={{
                height: 4,
                background: 'var(--color-bg-secondary)',
                borderRadius: 2,
                overflow: 'hidden',
                marginBottom: 14,
            }}>
                <div style={{
                    width: `${usedPct}%`,
                    height: '100%',
                    background: 'var(--color-status-on-leave)',
                }} />
            </div>
            <div className="flex flex-col" style={{ gap: 6, fontSize: 12 }}>
                <div className="flex justify-between">
                    <span style={{ color: 'var(--color-text-secondary)' }}>Wedding</span>
                    <span>{data.lifetime.wedding.remaining} days available</span>
                </div>
                <div className="flex justify-between">
                    <span style={{ color: 'var(--color-text-secondary)' }}>Umrah</span>
                    <span>{data.lifetime.umrah.remaining} days available</span>
                </div>
            </div>
        </div>
    );
}

function RequestLeaveCta() {
    return (
        <div className="surface-card flex flex-col" style={{ padding: '1rem 1.25rem' }}>
            <p style={{ fontSize: 14, fontWeight: 500, margin: 0, marginBottom: 8 }}>
                Request leave
            </p>
            <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 14, lineHeight: 1.5 }}>
                Submit a new leave request — casual, sick, vacation, parental, wedding, umrah, or unpaid. Half-day supported.
            </p>
            <Link to="/leave" className="btn btn-soft" style={{ alignSelf: 'flex-start' }}>
                Open leave page →
            </Link>
        </div>
    );
}

function RecentLeaveRequestsCard({ history, loading }) {
    if (loading) {
        return (
            <div className="surface-card" style={{ padding: '1rem 1.25rem', marginBottom: 24 }}>
                <p style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>My recent leave requests</p>
                <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 8 }}>Loading…</p>
            </div>
        );
    }
    const recent = (history?.data || []).slice(0, 3);
    return (
        <div className="surface-card" style={{ padding: '1rem 1.25rem', marginBottom: 24 }}>
            <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
                <p style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>My recent leave requests</p>
                <Link to="/leave" style={{ fontSize: 12, color: 'var(--color-text-info)' }}>View all</Link>
            </div>
            {recent.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                    No requests yet. Use “Open leave page” above to submit one.
                </p>
            ) : (
                <div className="flex flex-col" style={{ gap: 0 }}>
                    {recent.map((req, idx) => (
                        <div
                            key={req.id}
                            className="flex items-center justify-between"
                            style={{
                                padding: '8px 0',
                                borderBottom: idx === recent.length - 1
                                    ? 'none'
                                    : '0.5px solid var(--color-border-tertiary)',
                            }}
                        >
                            <div>
                                <p style={{ fontSize: 13, fontWeight: 500, margin: 0 }}>
                                    {capitalise(req.leave_type)} · {formatRange(req.start_date, req.end_date)}
                                </p>
                                <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                                    {req.working_days} {req.working_days === 1 ? 'day' : 'days'} · {statusLine(req)}
                                </p>
                            </div>
                            <span className={`pill pill-${req.status}`}>{req.status}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function statusLine(req) {
    if (req.status === 'pending') return 'awaiting approval';
    if (req.status === 'approved') return 'approved';
    if (req.status === 'rejected') return req.review_note ? `rejected — ${req.review_note}` : 'rejected';
    if (req.status === 'cancelled') return 'cancelled';
    return req.status;
}

function buildSessionMeta(checkInTimeStr, sessionsCount) {
    const parts = [];
    if (checkInTimeStr) parts.push(`Checked in at ${checkInTimeStr}`);
    if (sessionsCount && sessionsCount > 1) parts.push(`${sessionsCount} sessions today`);
    return parts.length > 0 ? parts.join(' · ') : 'Working now';
}

function tidyTime(hms) {
    if (!hms) return '';
    const [h, m] = hms.split(':');
    const hour = Number(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour % 12 || 12;
    return `${h12}:${m} ${ampm}`;
}

function formatRange(start, end) {
    if (start === end) return formatShortDate(start);
    return `${formatShortDate(start)} – ${formatShortDate(end)}`;
}

function formatShortDate(d) {
    if (!d) return '';
    return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatYearRange(yearStart) {
    if (!yearStart) return '';
    const [y] = yearStart.split('-').map(Number);
    return `Jul ${y} – Jun ${y + 1}`;
}

function CenteredMessage({ text, tone }) {
    const color = tone === 'error' ? 'var(--color-danger)' : 'var(--color-text-tertiary)';
    return (
        <div className="min-h-screen flex items-center justify-center" style={{ fontSize: 13, color }}>
            {text}
        </div>
    );
}
